import { useEffect } from 'react';

const LAST_UPDATED = '2026-05-01';
const CONTACT_EMAIL = 'yomayelluciano@gmail.com';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Política de Privacidad — TrainEase';
  }, []);

  return (
    <div
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '2.5rem 1.25rem 4rem',
        color: 'var(--ios-text)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        lineHeight: 1.6
      }}
    >
      <header style={{ marginBottom: '2rem' }}>
        <h1
          style={{
            fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            margin: 0
          }}
        >
          Política de Privacidad
        </h1>
        <p style={{ color: 'var(--ios-text-secondary)', marginTop: '0.5rem', fontSize: '0.9rem' }}>
          Última actualización: {LAST_UPDATED}
        </p>
      </header>

      <section style={{ marginBottom: '2rem' }}>
        <p>
          Esta Política de Privacidad describe cómo <strong>TrainEase</strong> (en adelante, &ldquo;la
          Aplicación&rdquo;, &ldquo;nosotros&rdquo; o &ldquo;el Servicio&rdquo;) recopila, usa y protege la información
          de las personas usuarias (en adelante, &ldquo;vos&rdquo; o &ldquo;el Usuario&rdquo;) que utilizan tanto la
          aplicación móvil como la versión web. Al usar TrainEase aceptás las prácticas descritas en este documento.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>1. Quiénes somos</h2>
        <p>
          TrainEase es una plataforma de coaching fitness que conecta a entrenadores con sus alumnos. El servicio es
          operado de forma independiente por su responsable. Para consultas, escribinos a{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>2. Datos que recopilamos</h2>
        <p>Para que el servicio funcione correctamente, podemos recopilar las siguientes categorías de datos:</p>
        <ul>
          <li>
            <strong>Datos de cuenta:</strong> correo electrónico, nombre, apellido, contraseña (almacenada de forma
            cifrada con hash), teléfono opcional, fecha de nacimiento, género, altura, peso, nivel de actividad física y
            objetivos de entrenamiento.
          </li>
          <li>
            <strong>Datos de entrenamiento:</strong> rutinas asignadas, sesiones realizadas, registros de series
            (repeticiones, peso, RPE/RIR), comentarios, sensaciones reportadas (energía, ánimo, dificultad) e historial
            de entrenamientos.
          </li>
          <li>
            <strong>Contenido subido por el Usuario:</strong> fotos o archivos (CSV/XLSX) que el Usuario sube
            voluntariamente para que la función de carga automática genere un plan usando inteligencia artificial.
          </li>
          <li>
            <strong>Datos técnicos mínimos:</strong> tokens de sesión cifrados (almacenados en el llavero seguro del
            dispositivo), idioma preferido y estado de conexión.
          </li>
        </ul>
        <p>
          <strong>NO</strong> recopilamos tu ubicación geográfica, contactos, ni rastreamos tu actividad fuera de la
          aplicación.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>3. Cómo usamos tus datos</h2>
        <ul>
          <li>Para crear y mantener tu cuenta y perfil.</li>
          <li>Para mostrarte tus entrenamientos asignados y registrar el progreso de tus sesiones.</li>
          <li>Para que tu coach pueda ver tu desempeño y ajustar tus rutinas.</li>
          <li>
            Para procesar las imágenes o planillas que subas mediante inteligencia artificial y devolverte un plan
            editable.
          </li>
          <li>
            Para autenticarte de forma segura (incluido el uso opcional de Face ID o huella digital, que se gestiona
            localmente en el dispositivo).
          </li>
          <li>Para mantener la sesión activa entre aperturas de la app y permitir el modo offline.</li>
        </ul>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>4. Servicios de terceros</h2>
        <p>TrainEase usa los siguientes servicios externos para funcionar:</p>
        <ul>
          <li>
            <strong>OpenAI:</strong> cuando usás la función de &ldquo;Cargar entrenamiento&rdquo;, el contenido que
            subís (imagen, CSV o XLSX) se envía al servicio de OpenAI exclusivamente para extraer la rutina y devolverla
            en formato editable. OpenAI puede procesar temporalmente el contenido según sus propias políticas. No se
            envía ningún dato personal adicional junto con la imagen.
          </li>
          <li>
            <strong>Apple TestFlight (durante la fase beta):</strong> Apple recopila datos básicos de uso y crashes de
            las apps distribuidas vía TestFlight. Consultá la política de Apple para más detalle.
          </li>
        </ul>
        <p>
          NO usamos servicios de publicidad, analítica de terceros, ni vendemos ni cedemos tus datos a terceros con
          fines comerciales.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>5. Permisos del dispositivo</h2>
        <ul>
          <li>
            <strong>Cámara:</strong> solo si elegís sacar una foto de tu rutina para procesarla con IA.
          </li>
          <li>
            <strong>Fotos:</strong> solo si elegís seleccionar una imagen existente de tu galería.
          </li>
          <li>
            <strong>Face ID / huella:</strong> opcional, para desbloquear la app más rápido. La biometría se procesa
            localmente; nunca sale del dispositivo.
          </li>
          <li>
            <strong>Notificaciones (próximamente):</strong> opcional, para recordatorios de entrenamiento.
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>6. Almacenamiento y seguridad</h2>
        <p>
          Tus datos se almacenan en servidores propios del responsable del Servicio. Las contraseñas se guardan con hash
          (bcrypt). Los tokens de sesión móviles se almacenan cifrados en el llavero seguro del sistema operativo
          (Keychain en iOS, EncryptedSharedPreferences en Android). La comunicación entre la app y el servidor viaja
          siempre por HTTPS.
        </p>
        <p>
          Aunque aplicamos medidas razonables de seguridad, ningún sistema es 100% inviolable. Si detectamos un
          incidente que afecte tus datos, te lo comunicaremos a la brevedad.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>7. Tus derechos</h2>
        <p>En cualquier momento podés:</p>
        <ul>
          <li>Acceder y modificar los datos de tu perfil desde la sección de Perfil de la app.</li>
          <li>
            Solicitar la eliminación total de tu cuenta y de los datos asociados escribiéndonos a{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </li>
          <li>Pedir una copia de tus datos.</li>
          <li>Retirar tu consentimiento al uso de la aplicación dejando de utilizarla y eliminando tu cuenta.</li>
        </ul>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>8. Menores de edad</h2>
        <p>
          TrainEase no está dirigida a personas menores de 16 años. Si sos menor de esa edad, por favor no uses el
          servicio sin el consentimiento de tu madre, padre o tutor legal.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>9. Cambios en esta política</h2>
        <p>
          Podemos actualizar esta política cuando agreguemos nuevas funciones o servicios. Cuando haya cambios
          significativos, los avisaremos dentro de la app o por correo. La fecha de la última actualización figura al
          inicio de este documento.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={sectionTitle}>10. Contacto</h2>
        <p>
          Si tenés cualquier pregunta sobre esta Política de Privacidad o sobre el manejo de tus datos, escribinos a{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>
    </div>
  );
};

const sectionTitle: React.CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  marginTop: 0,
  marginBottom: '0.75rem'
};

export default PrivacyPolicy;
